import { z } from "zod";

/**
 * Model Context Protocol contract shared by the API's MCP server, the CLI
 * daemon that configures agent runtimes, and the clients that manage tokens.
 *
 * Rivets speaks stateless Streamable HTTP: one JSON-RPC request per POST body,
 * no batching (removed in 2025-06-18), no session id, no server-initiated SSE.
 * Everything here is wire contract only — the tool registry itself lives in
 * `apps/api/src/mcp/registry.ts`, which must cover exactly `MCP_TOOL_NAMES`.
 */

export const MCP_PROTOCOL_VERSION = "2025-06-18";
/**
 * What a request with no `MCP-Protocol-Version` header is assumed to be
 * speaking. The Streamable HTTP spec pins this to `2025-03-26` — the last
 * version that predates the header — so a header-less client must NOT be
 * treated as if it had negotiated the newest version this server happens to
 * support.
 */
export const MCP_FALLBACK_PROTOCOL_VERSION = "2025-03-26";
/**
 * Versions the server will accept in `MCP-Protocol-Version`. Ordered newest
 * first: index 0 is what `initialize` answers with when it cannot honour the
 * client's request.
 *
 * Serving both is honest for this server because the only difference that
 * reaches the wire here is JSON-RPC batching, which `2025-03-26` permits and
 * `2025-06-18` removed: batches are refused with `-32600` under either version
 * (see `apps/api/src/mcp/jsonrpc.ts`), and a client that gets that answer
 * retries unbatched. Everything else that changed in `2025-06-18` and shows up
 * in a response — `structuredContent`, tool `title`, `_meta` — is additive and
 * ignored by an older client. The features that would genuinely diverge
 * (elicitation, resource links, server-initiated requests) are not implemented
 * at all.
 */
export const MCP_SUPPORTED_PROTOCOL_VERSIONS = [
  MCP_PROTOCOL_VERSION,
  MCP_FALLBACK_PROTOCOL_VERSION,
] as const;

/** Shared by the transport (header check) and the server (body negotiation),
 * so the two can never disagree about what "supported" means. */
export function isSupportedMcpProtocolVersion(version: string): boolean {
  return (MCP_SUPPORTED_PROTOCOL_VERSIONS as readonly string[]).includes(version);
}

export const MCP_SERVER_NAME = "rivets";
export const MCP_SERVER_TITLE = "Rivets";
/** Header carrying the negotiated version on every request after `initialize`. */
export const MCP_PROTOCOL_VERSION_HEADER = "mcp-protocol-version";

/** The five methods a stateless Streamable HTTP server has to implement. */
export const MCP_METHODS = {
  initialize: "initialize",
  initialized: "notifications/initialized",
  ping: "ping",
  toolsList: "tools/list",
  toolsCall: "tools/call",
} as const;
export type McpMethod = (typeof MCP_METHODS)[keyof typeof MCP_METHODS];

// ---------------------------------------------------------------------------
// JSON-RPC 2.0 envelopes
// ---------------------------------------------------------------------------

/** JSON-RPC permits string or number ids; `null` is only valid on a response
 * whose request could not be parsed. */
export const JsonRpcId = z.union([z.string().min(1).max(200), z.number()]);
export type JsonRpcId = z.infer<typeof JsonRpcId>;

/** An inbound message. `id` absent means it is a notification and MUST NOT be
 * answered with a response body. */
export const JsonRpcMessage = z.object({
  jsonrpc: z.literal("2.0"),
  id: JsonRpcId.optional(),
  method: z.string().min(1).max(120),
  params: z.unknown().optional(),
});
export type JsonRpcMessage = z.infer<typeof JsonRpcMessage>;

export const JsonRpcRequest = JsonRpcMessage.extend({ id: JsonRpcId });
export type JsonRpcRequest = z.infer<typeof JsonRpcRequest>;

export const JsonRpcNotification = JsonRpcMessage.omit({ id: true });
export type JsonRpcNotification = z.infer<typeof JsonRpcNotification>;

export const JsonRpcErrorObject = z.object({
  code: z.number().int(),
  message: z.string(),
  data: z.unknown().optional(),
});
export type JsonRpcErrorObject = z.infer<typeof JsonRpcErrorObject>;

export const JsonRpcSuccessResponse = z.object({
  jsonrpc: z.literal("2.0"),
  id: JsonRpcId,
  result: z.unknown(),
});
export type JsonRpcSuccessResponse = z.infer<typeof JsonRpcSuccessResponse>;

export const JsonRpcErrorResponse = z.object({
  jsonrpc: z.literal("2.0"),
  /** Null when the request was unparseable, so no id could be recovered. */
  id: JsonRpcId.nullable(),
  error: JsonRpcErrorObject,
});
export type JsonRpcErrorResponse = z.infer<typeof JsonRpcErrorResponse>;

export const JsonRpcResponse = z.union([JsonRpcSuccessResponse, JsonRpcErrorResponse]);
export type JsonRpcResponse = z.infer<typeof JsonRpcResponse>;

/** The standard JSON-RPC 2.0 codes. Protocol faults use these; *tool* failures
 * are successful responses carrying `isError: true`, so a model can recover. */
export const JSON_RPC_ERROR_CODES = {
  parseError: -32700,
  invalidRequest: -32600,
  methodNotFound: -32601,
  invalidParams: -32602,
  internalError: -32603,
} as const;
export type JsonRpcErrorCode = (typeof JSON_RPC_ERROR_CODES)[keyof typeof JSON_RPC_ERROR_CODES];

// ---------------------------------------------------------------------------
// Tool results
// ---------------------------------------------------------------------------

/** Rivets only ever emits text blocks; the field is kept open for images later. */
export const McpContentBlock = z.object({
  type: z.literal("text"),
  text: z.string(),
});
export type McpContentBlock = z.infer<typeof McpContentBlock>;

/** Every tool returns both `content` (text-only clients) and
 * `structuredContent` (structured clients) with the same payload. */
export const McpToolResult = z.object({
  content: z.array(McpContentBlock).min(1),
  structuredContent: z.unknown().optional(),
  isError: z.boolean().optional(),
});
export type McpToolResult = z.infer<typeof McpToolResult>;

// ---------------------------------------------------------------------------
// Scopes
// ---------------------------------------------------------------------------

/** Capabilities an API token may carry. `assistant` is only ever minted for a
 * Rivets assistant session and is never offered in the token UI. The `linear:*`
 * pair reaches the organization's *existing* Linear connection — a token never
 * carries Linear credentials of its own. */
export const ApiTokenScope = z.enum([
  "tasks:read",
  "tasks:write",
  "git:write",
  "assistant",
  "linear:read",
  "linear:write",
]);
export type ApiTokenScope = z.infer<typeof ApiTokenScope>;
export const API_TOKEN_SCOPES = ApiTokenScope.options;

/**
 * The scopes a person may actually ask for, in the order token UIs render them.
 * `assistant` is missing on purpose: it is minted by the server for its own
 * session credentials and `POST /orgs/:orgId/tokens` refuses it
 * (`invalid_scope`), so offering it as a checkbox only produces an error.
 */
export const SELECTABLE_API_TOKEN_SCOPES: ApiTokenScope[] = [
  "tasks:read",
  "tasks:write",
  "git:write",
  "linear:read",
  "linear:write",
];

/** What a personal token gets when the caller does not choose. */
export const DEFAULT_API_TOKEN_SCOPES: ApiTokenScope[] = ["tasks:read", "tasks:write"];
/** Scope set minted for an assistant session's own credential. */
export const ASSISTANT_API_TOKEN_SCOPES: ApiTokenScope[] = [
  "tasks:read",
  "tasks:write",
  "git:write",
  "assistant",
  // The assistant is the reason the Linear tools exist (FRNT-698): asking it
  // about issues must work without anyone wiring up a second MCP server.
  "linear:read",
  "linear:write",
];

/** Bearer prefixes. Personal tokens are user-created; assistant tokens are
 * minted per session, live 12 hours and are never displayed. */
export const API_TOKEN_PREFIX_PERSONAL = "rvp_";
export const API_TOKEN_PREFIX_ASSISTANT = "rva_";
/** Hex characters after the prefix — the same shape as a worker token. */
export const API_TOKEN_SECRET_HEX_LENGTH = 64;
/** How much of the hash-free secret is retained for display in token lists. */
export const API_TOKEN_DISPLAY_PREFIX_LENGTH = 8;

// ---------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------

/** Every tool the Rivets MCP server exposes. The api-side registry is checked
 * against this list, so adding a tool starts here. */
export const MCP_TOOL_NAMES = [
  "rivets_whoami",
  "rivets_list_repos",
  "rivets_list_workers",
  "rivets_list_tasks",
  "rivets_get_task",
  "rivets_get_task_output",
  "rivets_list_changes",
  "rivets_read_file",
  "rivets_create_task",
  "rivets_create_chat",
  "rivets_send_message",
  "rivets_cancel_task",
  "rivets_archive_task",
  "rivets_restore_task",
  "rivets_watch_task",
  "rivets_unwatch_task",
  "rivets_prepare_pr",
  "rivets_save_pr_draft",
  "rivets_run_pr_action",
  "rivets_update_pr",
  "rivets_pr_workspace",
  "rivets_merge_pull_request",
  "rivets_discard_changes",
  // Appended, never reordered: `tools/list` order is stable across deploys and
  // a client's cached ordering must not churn.
  "rivets_linear_list_teams",
  "rivets_linear_list_projects",
  "rivets_linear_list_issues",
  "rivets_linear_get_issue",
  "rivets_linear_create_issue",
  "rivets_linear_update_issue",
  "rivets_linear_create_comment",
] as const;

export const McpToolName = z.enum(MCP_TOOL_NAMES);
export type McpToolName = z.infer<typeof McpToolName>;

// ---------------------------------------------------------------------------
// Per-job MCP configuration
// ---------------------------------------------------------------------------

/** How many MCP servers one job may be given. Bounds the JobSpec frame and the
 * amount of config the daemon has to materialize before spawning an agent. */
export const MAX_JOB_MCP_SERVERS = 4;

/** One MCP server handed to an agent runtime on `JobSpec.mcp`. Only streamable
 * HTTP is supported: stdio would mean shipping executables to the worker. */
export const McpServerConfig = z.object({
  name: z.string().regex(/^[a-z][a-z0-9_-]*$/).max(40),
  transport: z.literal("http"),
  url: z.string().url(),
  /** Never logged and never placed in argv; the daemon writes these to a 0600
   * file or a child-process env var. */
  headers: z.record(z.string().min(1).max(64), z.string().max(4_096)),
});
export type McpServerConfig = z.infer<typeof McpServerConfig>;
